import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Page from '../../components/page/page'
import PageHeader from '../../components/page/pageHeader'
import TabsWithPermissions, { Tab } from '../../components/tabsWithPermissions/tabsWithPermissions'
import { enumToRecord, recordToMap } from '../../helpers'
import { hasResource } from '../../helpers/permissionsHelper'
import { useErrorDetailListDialog, useHttpError, usePageState } from '../../hooks'
import { Resources } from '../../model/RBAC/constants'
import { storeActiveTabForCurrentPage, storeCustomFilterForCurrentPage } from '../../redux'
import { TradesTable } from './components/tradesTable/TradesTable'

export interface TradesPageProps {}

export enum TRADES_TABS {
  ALL = 0,
  GENERAL = 1,
  EXTENDED_CLIENT = 2
}

export const TradesPage: React.FC<TradesPageProps> = () => {
  const [t] = useTranslation(['globalerisLibrary', 'trades'])
  const { errorProps } = useHttpError()
  const { dataTableFilter, dataTableSearch, dateFilter, customFilter } = usePageState()
  const { errorDetailListDialogProps } = useErrorDetailListDialog()

  const { activeTab } = usePageState()
  const [currentTab, setCurrentTab] = useState<TRADES_TABS | null>(null)

  //#region Handler
  const tabs = enumToRecord(TRADES_TABS)

  const tabsDefinition: Record<number, Tab> = Object.keys(tabs).reduce((acc, typeName, index) => {
    const tabId = tabs[typeName]
    let show = false

    switch (tabId) {
      case TRADES_TABS.EXTENDED_CLIENT:
        show = hasResource(Resources.ExtendedClients)
        break
      case TRADES_TABS.GENERAL:
        show = hasResource(Resources.ExtendedClients)
        break
      case TRADES_TABS.ALL:
        show = !hasResource(Resources.ExtendedClients)
        break
      default:
        show = true
        break
    }

    return show
      ? {
          ...acc,
          [index]: {
            id: tabId,
            label: t(`trades:tabs.${typeName.toLowerCase()}`),
            hasPermission: show
          }
        }
      : acc
  }, {})

  const onTabChangeHandler = (tabIdx: number) => {
    const tab = Object.values(tabsDefinition)[tabIdx]
    // const tab = Object.values(tabsDefinition).find(tab => tab.id === tabIdx)
    if (tab) {
      setCurrentTab(prevTab => {
        if (prevTab !== tab.id) {
          storeActiveTabForCurrentPage(tabIdx)
          // Clear custom filter if active
          if (customFilter) {
            storeCustomFilterForCurrentPage(undefined)
          }
        }
        return tab.id as TRADES_TABS
      })
    }
  }

  useEffect(() => {
    if (activeTab !== undefined && currentTab === null) {
      const tab = Object.values(tabsDefinition)[activeTab]
      if (tab) {
        setCurrentTab(tab.id as TRADES_TABS)
      }
    }
  }, [activeTab])

  const getVisibleTabsCount = (tabs: Map<number, Tab>): number => {
    return [...tabs.values()].filter(tab => tab.hasPermission).length
  }

  return (
    <Page>
      <PageHeader title={t('trades:title')} />
      <TabsWithPermissions
        tabs={recordToMap(tabsDefinition)}
        tabDefault={activeTab ?? 0}
        onTabChange={onTabChangeHandler}
        hideTabs={getVisibleTabsCount(recordToMap(tabsDefinition)) <= 1}
      />
      <TradesTable
        currentTab={currentTab}
        errorProps={errorProps}
        dataTableFilter={dataTableFilter}
        dataTableSearch={dataTableSearch ?? ''}
        customFilter={customFilter}
        dateFilter={dateFilter}
        errorDetailListDialogProps={errorDetailListDialogProps}
      />
    </Page>
  )
}
